import React, { useState } from 'react';
import { X, Palette, Type, Layout as LayoutIcon, Save } from 'lucide-react';
import Button from '../common/Button';
import Input from '../common/Input';

const CreateTemplateModal = ({ isOpen, onClose, onSave, loading }) => {
  const [formData, setFormData] = useState({
    template_name: '',
    template_type: 'standard',
    primaryColor: '#3B82F6',
    secondaryColor: '#1F2937',
    backgroundColor: '#FFFFFF',
    textColor: '#000000',
    headerHeight: 50,
    showQR: true,
    showLogo: true,
    qrSize: 100,
    qrPosition: 'bottom-center',
    nameSize: 18,
    fieldSize: 11,
  });

  const [errors, setErrors] = useState({});

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const validate = () => {
    const newErrors = {};
    if (!formData.template_name.trim()) {
      newErrors.template_name = 'Template name is required';
    }
    return newErrors;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const newErrors = validate();
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    const templateData = {
      template_name: formData.template_name,
      template_type: formData.template_type,
      design_config: {
        primaryColor: formData.primaryColor,
        secondaryColor: formData.secondaryColor,
        backgroundColor: formData.backgroundColor,
        textColor: formData.textColor,
        headerHeight: formData.headerHeight,
        showQRCode: formData.showQR,
        showLogo: formData.showLogo,
        qrSize: formData.qrSize,
        qrPosition: formData.qrPosition,
        nameSize: formData.nameSize,
        fieldSize: formData.fieldSize,
      },
    };

    onSave(templateData);
  };

  const handleClose = () => {
    setFormData({
      template_name: '',
      template_type: 'standard',
      primaryColor: '#3B82F6',
      secondaryColor: '#1F2937',
      backgroundColor: '#FFFFFF',
      textColor: '#000000',
      headerHeight: 50,
      showQR: true,
      showLogo: true,
      qrSize: 100,
      qrPosition: 'bottom-center',
      nameSize: 18,
      fieldSize: 11,
    });
    setErrors({});
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex min-h-screen items-center justify-center p-4">
        {/* Backdrop */}
        <div
          className="fixed inset-0 bg-black bg-opacity-50 transition-opacity"
          onClick={handleClose}
        />

        {/* Modal */}
        <div className="relative bg-white rounded-lg shadow-xl w-full max-w-6xl transform transition-all">
          {/* Header */}
          <div className="flex items-center justify-between p-6 border-b">
            <div className="flex items-center gap-2">
              <Palette className="h-6 w-6 text-primary-600" />
              <h3 className="text-xl font-semibold text-gray-900">Create Badge Template</h3>
            </div>
            <button
              onClick={handleClose}
              className="text-gray-400 hover:text-gray-600 transition-colors"
              disabled={loading}
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          {/* Content */}
          <form onSubmit={handleSubmit}>
            <div className="p-6 grid grid-cols-1 lg:grid-cols-2 gap-6 max-h-[calc(100vh-200px)] overflow-y-auto">
              {/* Left Column - Form */}
              <div className="space-y-6">
                {/* Basic Info */}
                <div>
                  <h4 className="text-sm font-semibold text-gray-900 mb-3 flex items-center gap-2">
                    <Type className="h-4 w-4" />
                    Basic Information
                  </h4>
                  <div className="space-y-4">
                    <Input
                      label="Template Name"
                      value={formData.template_name}
                      onChange={(e) => handleChange('template_name', e.target.value)}
                      error={errors.template_name}
                      required
                      placeholder="e.g., VIP Gold Badge"
                    />
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Template Type
                      </label>
                      <select
                        value={formData.template_type}
                        onChange={(e) => handleChange('template_type', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                      >
                        <option value="standard">Standard</option>
                        <option value="vip">VIP</option>
                        <option value="speaker">Speaker</option>
                        <option value="staff">Staff</option>
                      </select>
                    </div>
                  </div>
                </div>

                {/* Colors */}
                <div>
                  <h4 className="text-sm font-semibold text-gray-900 mb-3 flex items-center gap-2">
                    <Palette className="h-4 w-4" />
                    Colors
                  </h4>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Primary Color
                      </label>
                      <div className="flex gap-2">
                        <input
                          type="color"
                          value={formData.primaryColor}
                          onChange={(e) => handleChange('primaryColor', e.target.value)}
                          className="h-10 w-16 rounded border border-gray-300 cursor-pointer"
                        />
                        <input
                          type="text"
                          value={formData.primaryColor}
                          onChange={(e) => handleChange('primaryColor', e.target.value)}
                          className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                        />
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Secondary Color
                      </label>
                      <div className="flex gap-2">
                        <input
                          type="color"
                          value={formData.secondaryColor}
                          onChange={(e) => handleChange('secondaryColor', e.target.value)}
                          className="h-10 w-16 rounded border border-gray-300 cursor-pointer"
                        />
                        <input
                          type="text"
                          value={formData.secondaryColor}
                          onChange={(e) => handleChange('secondaryColor', e.target.value)}
                          className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                        />
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Background Color
                      </label>
                      <div className="flex gap-2">
                        <input
                          type="color"
                          value={formData.backgroundColor}
                          onChange={(e) => handleChange('backgroundColor', e.target.value)}
                          className="h-10 w-16 rounded border border-gray-300 cursor-pointer"
                        />
                        <input
                          type="text"
                          value={formData.backgroundColor}
                          onChange={(e) => handleChange('backgroundColor', e.target.value)}
                          className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                        />
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Text Color
                      </label>
                      <div className="flex gap-2">
                        <input
                          type="color"
                          value={formData.textColor}
                          onChange={(e) => handleChange('textColor', e.target.value)}
                          className="h-10 w-16 rounded border border-gray-300 cursor-pointer"
                        />
                        <input
                          type="text"
                          value={formData.textColor}
                          onChange={(e) => handleChange('textColor', e.target.value)}
                          className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                        />
                      </div>
                    </div>
                  </div>
                </div>

                {/* Layout Settings */}
                <div>
                  <h4 className="text-sm font-semibold text-gray-900 mb-3 flex items-center gap-2">
                    <LayoutIcon className="h-4 w-4" />
                    Layout & Sizing
                  </h4>
                  <div className="space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Header Height (px)
                        </label>
                        <input
                          type="number"
                          min="30"
                          max="100"
                          value={formData.headerHeight}
                          onChange={(e) => handleChange('headerHeight', parseInt(e.target.value))}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Name Font Size (px)
                        </label>
                        <input
                          type="number"
                          min="12"
                          max="28"
                          value={formData.nameSize}
                          onChange={(e) => handleChange('nameSize', parseInt(e.target.value))}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Field Font Size (px)
                        </label>
                        <input
                          type="number"
                          min="8"
                          max="16"
                          value={formData.fieldSize}
                          onChange={(e) => handleChange('fieldSize', parseInt(e.target.value))}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          QR Code Size (px)
                        </label>
                        <input
                          type="number"
                          min="60"
                          max="140"
                          value={formData.qrSize}
                          onChange={(e) => handleChange('qrSize', parseInt(e.target.value))}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                        />
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        QR Code Position
                      </label>
                      <select
                        value={formData.qrPosition}
                        onChange={(e) => handleChange('qrPosition', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                      >
                        <option value="bottom-center">Bottom Center</option>
                        <option value="bottom-left">Bottom Left</option>
                        <option value="bottom-right">Bottom Right</option>
                        <option value="middle-right">Middle Right</option>
                      </select>
                    </div>

                    <div className="space-y-2">
                      <label className="flex items-center gap-2">
                        <input
                          type="checkbox"
                          checked={formData.showQR}
                          onChange={(e) => handleChange('showQR', e.target.checked)}
                          className="h-4 w-4 text-primary-600 rounded border-gray-300 focus:ring-primary-500"
                        />
                        <span className="text-sm text-gray-700">Show QR Code</span>
                      </label>
                      <label className="flex items-center gap-2">
                        <input
                          type="checkbox"
                          checked={formData.showLogo}
                          onChange={(e) => handleChange('showLogo', e.target.checked)}
                          className="h-4 w-4 text-primary-600 rounded border-gray-300 focus:ring-primary-500"
                        />
                        <span className="text-sm text-gray-700">Show Event Logo</span>
                      </label>
                    </div>
                  </div>
                </div>
              </div>

              {/* Right Column - Preview */}
              <div>
                <h4 className="text-sm font-semibold text-gray-900 mb-3">Live Preview</h4>
                <div className="sticky top-0">
                  <div className="bg-gray-50 rounded-lg p-6 border-2 border-gray-200">
                    <div className="aspect-[3/4] flex items-center justify-center">
                      <div 
                        className="w-full h-full rounded-lg shadow-lg"
                        style={{ 
                          backgroundColor: formData.backgroundColor,
                          maxWidth: '280px',
                        }}
                      >
                        {/* Header */}
                        <div 
                          className="w-full flex items-center justify-center"
                          style={{ 
                            backgroundColor: formData.primaryColor,
                            height: `${formData.headerHeight}px`,
                          }}
                        >
                          <p className="text-white text-xs font-medium">Conference 2025</p>
                        </div>

                        {/* Name */}
                        <div className="px-4 mt-4">
                          <p 
                            className="font-bold text-center"
                            style={{ 
                              color: formData.secondaryColor,
                              fontSize: `${formData.nameSize}px`,
                            }}
                          >
                            Albert Kwame Tchalla
                          </p>
                        </div>

                        {/* Fields */}
                        <div className="px-4 mt-3 space-y-2">
                          <div className="text-center">
                            <p className="text-xs text-gray-500">Company</p>
                            <p 
                              className="font-semibold"
                              style={{ 
                                color: formData.textColor,
                                fontSize: `${formData.fieldSize}px`,
                              }}
                            >
                              Tech Corp
                            </p>
                          </div>
                          <div className="text-center">
                            <p className="text-xs text-gray-500">Title</p>
                            <p 
                              className="font-semibold"
                              style={{ 
                                color: formData.textColor,
                                fontSize: `${formData.fieldSize}px`,
                              }}
                            >
                              Senior Developer
                            </p>
                          </div>
                        </div>

                        {/* QR Code */}
                        {formData.showQR && (
                          <div className="flex justify-center mt-4">
                            <div 
                              className="bg-gray-300 rounded flex items-center justify-center"
                              style={{ 
                                width: `${formData.qrSize}px`,
                                height: `${formData.qrSize}px`,
                              }}
                            >
                              <span className="text-xs text-gray-600">QR</span>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Actions */}
            <div className="flex gap-3 p-6 border-t bg-gray-50">
              <Button
                type="button"
                variant="secondary"
                onClick={handleClose}
                className="flex-1"
                disabled={loading}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                className="flex-1 gap-2"
                loading={loading}
              >
                <Save className="h-5 w-5" />
                Create Template
              </Button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default CreateTemplateModal;
